import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { adminAPI, userAPI } from '../services/api';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeSubscriptions: 0,
    totalRevenue: 0,
    totalGenerations: 0,
  });
  const [analytics, setAnalytics] = useState({
    generationsByType: [],
    generationsByModel: []
  });
  const [recentUsers, setRecentUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch dashboard stats
      const statsResponse = await adminAPI.getDashboard();
      if (statsResponse.data.success) {
        setStats(statsResponse.data.data);
      }

      // Fetch analytics data
      const analyticsResponse = await adminAPI.getAnalytics();
      if (analyticsResponse.data.success) {
        setAnalytics(analyticsResponse.data.data);
      }

      // Fetch recent users
      const usersResponse = await userAPI.getAll({ limit: 5, sortBy: 'created_at', order: 'desc' });
      if (usersResponse.data.success) {
        setRecentUsers(usersResponse.data.data.users || []);
      }
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      if (err.response?.status !== 401) {
        setError('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      name: 'Total Users',
      value: stats.totalUsers?.toLocaleString() || '0',
      icon: (
        <svg viewBox="0 0 1024 1024" className="w-10 h-10">
          <path d="M632 344m-120 0a120 120 0 1 0 240 0 120 120 0 1 0-240 0Z" fill="#A9D8FF"></path>
          <path d="M809 800H503c-48.1 0-87-39-87-87 0-111 90-201 201-201h78c111 0 201 90 201 201 0 48-39 87-87 87z" fill="#A9D8FF"></path>
          <path d="M440 296m-168 0a168 168 0 1 0 336 0 168 168 0 1 0-336 0Z" fill="#298DF7"></path>
          <path d="M638.2 896H241.8C179 896 128 845 128 782.2 128 633 249 512 398.2 512h83.6C631 512 752 633 752 782.2 752 845 701 896 638.2 896z" fill="#298DF7"></path>
        </svg>
      ),
      bgColor: 'bg-blue-50',
      iconBgColor: 'bg-blue-100',
      textColor: 'text-blue-600'
    },
    {
      name: 'Active Subscriptions',
      value: stats.activeSubscriptions?.toLocaleString() || '0',
      icon: (
        <svg viewBox="0 0 1024 1024" className="w-10 h-10">
          <path d="M629.184 593.9392a38.4 38.4 0 0 0 24.4032-28.3072l23.1424-112.192a12.8 12.8 0 0 0-21.7472-11.4688l-44.544 46.1824-60.64-99.7504a38.4 38.4 0 0 0-65.6256 0.0064l-61.4656 101.1456-45.9008-47.584a12.8 12.8 0 0 0-21.7472 11.4752l23.1424 112.192a38.4064 38.4064 0 0 0 29.568 29.7856 38.4 38.4 0 0 0 16.576 3.776l185.3312 0.0448a38.2208 38.2208 0 0 0 19.5008-5.312z m234.4-65.4848a77.3056 77.3056 0 0 1-7.7888 10.6304l-286.3616 328.1472c-27.8912 31.9552-76.4032 35.2512-108.3648 7.36a76.8 76.8 0 0 1-7.36-7.36L167.328 539.0848a76.992 76.992 0 0 1-10.3168-15.1168 76.448 76.448 0 0 1-13.824-43.968 76.8 76.8 0 0 1 5.6576-28.9344l104.128-256A76.8 76.8 0 0 1 324.128 147.2h375.7312a76.8 76.8 0 0 1 71.1424 47.8656l104.128 256c10.9376 26.88 5.5424 56.3456-11.5392 77.3888zM511.936 326.4064a38.4 38.4 0 0 0 38.4-38.4 38.4 38.4 0 1 0-38.4 38.4z" fill="#f4ea2a"></path>
        </svg>
      ),
      bgColor: 'bg-purple-50',
      iconBgColor: 'bg-purple-100',
      textColor: 'text-purple-600'
    },
    {
      name: 'Total Revenue',
      value: `$${stats.totalRevenue?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '0.00'}`,
      icon: (
        <svg viewBox="0 0 1024 1024" className="w-10 h-10">
          <path d="M762.1 109.1c-9.9-11.2-21-19.7-35.4-26.9-12.1-4.9-26.4-7.6-43.4-8.5-17-0.9-36.3 4-58.2 16.1-14.3 7.2-34 17.9-58.7 31.3l-76.6 40.3c-29.6 16.1-60.9 32.7-94.5 49.7h419.6l-52.8-102z m-26 572.2c-22.8 0-40.3-4.9-52.4-13.4-12.1-9.4-20.6-19.3-26.4-30.4-6.3-13.4-9.9-28.2-10.7-45.2 0-24.6 8.5-46.1 26.4-63.1s38.1-25.5 63.6-25.5H960V368.8c0-24.6-8.5-46.1-26.4-63.6s-38.1-26.4-63.6-26.4H154c-24.6 0-46.1 8.5-63.6 26.4C73 323.1 64 343.2 64 368.8v492.1c0 12.5 2.7 24.2 7.2 34.9 4.9 10.7 11.2 20.6 20.6 28.2 8.5 8.5 18.4 14.8 29.6 19.3 11.2 4.9 22.8 7.2 35.4 7.2h716c12.5 0 24.2-2.2 34.9-7.2 10.7-4.9 19.7-10.7 27.8-19.3 7.6-8.5 14.3-17.9 18.4-28.2 4-10.7 6.3-22.8 6.3-34.9V681.8c-0.2-0.5-224.1-0.5-224.1-0.5z m-409.7 47.9c-76.6 0-138.4-61.8-138.4-138.4 0-76.6 61.8-138.4 138.4-138.4 76.6 0 138.4 61.8 138.4 138.4 0 76.2-62.7 138.4-138.4 138.4zM712.8 560c-9.4 9.4-13.4 19.7-13.4 32.7 0 12.5 4 24.2 13.4 33.1 9.4 9.4 19.7 14.3 32.7 14.3 12.5 0 24.2-4.9 32.7-14.3 9.4-9.4 13.4-20.6 13.4-33.1s-4-24.2-13.4-32.7c-9.4-9.4-19.7-13.4-32.7-13.4-13-0.1-23.7 4-32.7 13.4z m-380.6 17c-20.6-8.5-31.3-18.4-31.3-28.2 1.3-15.7 9.4-24.2 23.3-25.5 17 0 25.5 9.9 25.5 29.6h25.5c-1.3-29.1-14.3-46.1-38.1-49.7v-17.9h-17.9v18.4c-26.9 2.7-40.3 17.9-41.2 46.1 0 19.7 14.3 35.4 43.4 46.6 22.8 9.4 34 20.6 34 32.7-0.9 17-10.7 26.4-29.1 26.9-17.9-0.9-26.9-10.7-26.9-29.1h-24.2c0.9 31.3 14.8 48.4 42.5 49.7v19.7h18.4v-19.7c26.9-2.7 41.2-18.4 43.4-48.4-1.6-19.9-16.8-37.8-47.3-51.2z m0 0" fill="#1296db"></path>
        </svg>
      ),
      bgColor: 'bg-green-50',
      iconBgColor: 'bg-green-100',
      textColor: 'text-green-600'
    },
    {
      name: 'Total Generations',
      value: stats.totalGenerations?.toLocaleString() || '0',
      icon: (
        <svg viewBox="0 0 1024 1024" className="w-10 h-10">
          <path d="M0 458.9568c83.0464-32.3584 164.352-64.3072 248.32-84.3776 57.6512-13.9264 84.7872-43.8272 98.304-103.8336 20.0704-89.3952 49.7664-176.5376 77.1072-270.7456 5.632 15.5648 9.3184 25.2928 12.1856 34.9184 27.7504 96.0512 55.5008 191.6928 82.6368 287.744 13.6192 48.7424 92.9792 53.3504 132.096 66.2528 65.8432 21.1968 131.4816 43.008 205.1072 66.9696-16.6912 6.2464-25.3952 9.728-34.2016 12.8-65.536 21.2992-280.7808 43.3152-303.8208 123.904-26.3168 96.4608-54.6816 192-82.6368 287.744-2.4576 9.3184-6.4512 18.1248-11.6736 31.744-32.3584-111.2064-63.6928-216.8832-93.2864-322.9696-22.2208-76.8-240.128-100.2496-303.5136-120.832L0 458.9568z m738.7136 300.544c22.3232-6.2464 32.1536-18.2272 37.6832-41.0624 9.8304-40.6528 22.6304-80.5888 34.304-120.4224 1.4336-5.12 4.4032-9.216 8.3968-18.1248 11.776 39.0144 25.2928 71.9872 30.5152 106.3936 7.4752 49.9712 32.0512 75.1616 78.848 83.6608 32.5632 6.144 63.5904 20.1728 95.5392 30.6176-22.016 13.1072-43.6224 19.7632-64.8192 27.0336-30.0032 10.1376-67.584 12.3904-87.3472 33.28-19.7632 20.992-21.9136 60.928-31.4368 92.7744L819.6096 1024a1336.5248 1336.5248 0 0 1-45.056-141.0048c-5.12-19.6608-14.6432-29.3888-33.5872-34.9184-43.9296-12.288-87.1424-27.7504-130.2528-42.0864v-5.8368c42.8032-13.5168 84.8896-29.0816 128-40.6528z m22.016-562.8928c16.5888 4.608 23.8592 13.6192 27.9552 30.5152 7.2704 30.3104 16.896 59.904 25.4976 89.6 1.1264 3.6864 3.2768 6.8608 6.2464 13.4144 8.704-29.0816 18.8416-53.4528 22.6304-79.0528 5.632-37.0688 23.9616-55.9104 58.6752-62.1568 24.2688-4.608 47.4112-15.0528 70.9632-22.8352-16.2816-9.728-32.256-14.6432-48.128-20.0704-22.2208-7.4752-50.176-9.216-65.024-24.6784-14.5408-15.6672-16.1792-45.3632-23.2448-68.9152L820.9408 0a967.68 967.68 0 0 0-33.6896 104.7552c-3.7888 14.7456-10.8544 21.9136-24.9856 25.9072-32.5632 9.216-64.6144 20.7872-96.6656 31.3344v4.4032c31.8464 10.1376 63.0784 21.6064 95.1296 30.208z m0 0" fill="#1afa29"></path>
        </svg>
      ),
      bgColor: 'bg-orange-50',
      iconBgColor: 'bg-orange-100',
      textColor: 'text-orange-600'
    },
  ];

  // Format content type names
  const formatContentType = (type) => {
    const typeMap = {
      'ad-copy': 'AD Copy',
      'email': 'Email',
      'blog': 'Blog Posts',
      'product': 'Product Descriptions',
      'poster': 'Poster',
      'market-survey': 'Market Survey',
      'image-generation': 'AI Image Generation',
      'image-composition': 'Image Composition',
      'speech-synthesis': 'Speech Synthesis',
      'ai-avatar': 'AI Avatar'
    };
    return typeMap[type] || type;
  };

  // Calculate total for percentages
  const totalGenerationsByType = analytics.generationsByType.reduce((sum, item) => sum + parseInt(item.count), 0);
  const totalGenerationsByModel = analytics.generationsByModel.reduce((sum, item) => sum + parseInt(item.count), 0);

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header title="Dashboard" />
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading dashboard...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        <Header title="Dashboard" />

        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
              {error}
            </div>
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {statCards.map((stat) => (
              <div key={stat.name} className={`${stat.bgColor} rounded-xl shadow-sm border border-gray-100 p-6 transition-all hover:shadow-md`}>
                <div className="flex items-center justify-between mb-4">
                  <div className={`${stat.iconBgColor} p-3 rounded-lg`}>
                    {stat.icon}
                  </div>
                </div>
                <h3 className="text-gray-600 text-sm font-medium mb-2">{stat.name}</h3>
                <p className={`text-3xl font-bold ${stat.textColor}`}>{stat.value}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Generations by Content Type */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Generations by Content Type</h3>
                <span className="text-sm text-gray-500">{totalGenerationsByType.toLocaleString()} total</span>
              </div>
              <div className="space-y-4">
                {analytics.generationsByType.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No generation data yet</p>
                ) : (
                  analytics.generationsByType.map((item) => {
                    const percentage = totalGenerationsByType > 0 ? (parseInt(item.count) / totalGenerationsByType * 100).toFixed(1) : 0;
                    return (
                      <div key={item.content_type} className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-700">{formatContentType(item.content_type)}</span>
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-600">{parseInt(item.count).toLocaleString()}</span>
                            <span className="text-xs text-gray-500">({percentage}%)</span>
                          </div>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Generations by AI Model */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Generations by AI Model</h3>
                <span className="text-sm text-gray-500">{totalGenerationsByModel.toLocaleString()} total</span>
              </div>
              <div className="space-y-4">
                {analytics.generationsByModel.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No model usage data yet</p>
                ) : (
                  analytics.generationsByModel.map((item) => {
                    const percentage = totalGenerationsByModel > 0 ? (parseInt(item.count) / totalGenerationsByModel * 100).toFixed(1) : 0;
                    return (
                      <div key={item.name} className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-700">{item.name}</span>
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-600">{parseInt(item.count).toLocaleString()}</span>
                            <span className="text-xs text-gray-500">({percentage}%)</span>
                          </div>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-gradient-to-r from-purple-500 to-purple-600 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Recent Users */}
            <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Recent Users</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider pb-3">User</th>
                      <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider pb-3">Plan</th>
                      <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider pb-3">Joined</th>
                      <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider pb-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentUsers.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="text-gray-500 text-center py-8">No users yet</td>
                      </tr>
                    ) : (
                      recentUsers.map((user) => (
                        <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50 transition">
                          <td className="py-4">
                            <div>
                              <p className="font-medium text-gray-900">{user.full_name || 'N/A'}</p>
                              <p className="text-sm text-gray-500">{user.email}</p>
                            </div>
                          </td>
                          <td className="py-4">
                            <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                              user.plan_name === 'Professional' ? 'bg-purple-100 text-purple-800' :
                              user.plan_name === 'Basic' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {user.plan_name || 'Free'}
                            </span>
                          </td>
                          <td className="py-4">
                            <p className="text-sm text-gray-700">
                              {new Date(user.created_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </p>
                          </td>
                          <td className="py-4">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              <span className={`w-1.5 h-1.5 mr-1.5 rounded-full ${
                                user.is_active ? 'bg-green-400' : 'bg-red-400'
                              }`}></span>
                              {user.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Quick Stats</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Users</p>
                      <p className="text-lg font-bold text-gray-900">{stats.totalUsers}</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Subscriptions</p>
                      <p className="text-lg font-bold text-gray-900">{stats.activeSubscriptions}</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Revenue</p>
                      <p className="text-lg font-bold text-gray-900">${stats.totalRevenue?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '0.00'}</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Generations</p>
                      <p className="text-lg font-bold text-gray-900">{stats.totalGenerations?.toLocaleString() || '0'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
